/**
 * A centralized service for all API calls to the LessonPlanController.
 */
const API_BASE_URL = '/web/ui/lessonPlan';

/**
 * Fetches the entire configuration object for the wizard from the backend.
 * This includes all dropdowns, radio buttons, and checkbox options.
 */
export const getWizardConfig = async () => {
    const response = await fetch(`${API_BASE_URL}/configOptions`);
    if (!response.ok) {
        throw new Error('Failed to fetch wizard configuration');
    }
    return response.json();
};

/**
 * Fetches the curriculum data (subjects, textbooks, chapters, etc.).
 */
export const getCurriculumData = async () => {
    const response = await fetch(`${API_BASE_URL}/curriculum`);
    if (!response.ok) {
        throw new Error('Failed to fetch curriculum data');
    }
    return response.json();
};

/**
 * Fetches the library of instructional blocks (e.g., Think-Pair-Share).
 * If a templateId is provided, it will fetch blocks relevant to that template.
 * @param {string} [lessonPlanTemplateId] - The optional ID of the lesson plan template.
 */
export const getInstructionalBlocks = async (lessonPlanTemplateId) => {
    let url = `${API_BASE_URL}/instructionalBlocks`;
    if (lessonPlanTemplateId) {
        url += `?lessonPlanTemplateId=${encodeURIComponent(lessonPlanTemplateId)}`;
    }
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error('Failed to fetch instructional blocks');
    }
    return response.json();
};

/**
 * Fetches a paginated list of lesson plans from the library.
 *
 * @param {object} searchParameters - The search filter object.
 * (e.g., { subject: 'Mathematics', isArchived: false })
 * @param {number} [pageNumber=1] - The 1-based page number to retrieve.
 * @returns {Promise<object>} A Promise that resolves to the full PageInfo object
 * from the server.
 * - The list of lesson plans is in: `result.content`
 * - The total number of plans is in: `result.totalResults`
 * - The total number of pages is in: `result.totalPages`
 * - The current page (1-based) is in: `result.currPage`
 */
export const searchLessonPlans = async (searchParameters = {}, pageNumber = 1) => {
    // The controller expects the pageNumber to be *inside* the
    // LessonPlanSearchParameters DTO in the request body.
    const requestBody = {
        ...searchParameters,
        pageNumber: pageNumber,
    };

    const response = await fetch(`${API_BASE_URL}/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
        const errorData = await response.text();
        console.error('Failed to fetch lesson plan library:', errorData);
        throw new Error(`Failed to fetch lesson plan library (status: ${response.status})`);
    }


    return response.json();
};

/**
 * Fetches the full details (content and config) of a single lesson plan by its UUID.
 * @param {string} lessonPlanUuid - The UUID of the lesson plan to fetch.
 * @returns {Promise<object>} The full lesson plan object (FullLessonPlanResponse DTO).
 */
export const getLessonPlanDetails = async (lessonPlanUuid) => {
    const response = await fetch(`${API_BASE_URL}/${lessonPlanUuid}`);
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to fetch lesson plan details for ${lessonPlanUuid}`);
    }
    return response.json();
};

/**
 * Initiates the asynchronous lesson plan generation.
 * @param {object} config - The LessonPlanConfig DTO.
 * @returns {Promise<object>} The LessonPlanConfig object including the UUID.
 */
export const startLessonPlanGeneration = async (config) => {
    // Path changed from /generate/start to /generate
    const response = await fetch(`${API_BASE_URL}/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to start lesson plan generation.');
    }
    return response.json();
};

/**
 * Polls the backend for the status of a lesson plan generation task.
 * @param {string} lessonPlanUuid - The UUID of the task.
 * @returns {Promise<object>} The status response from the server.
 */
export const getGenerationStatus = async (lessonPlanUuid) => {
    const response = await fetch(`${API_BASE_URL}/status/${lessonPlanUuid}`);

    // For 202 Accepted (Pending), response.json() might fail if body is empty
    // The controller now returns a body for PENDING, but this check is safe.
    if (response.status === 202) {
        return response.json(); // e.g., { status: 'PENDING' }
    }

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Use errorData.error as the refactored controller no longer uses ApiError record
        throw new Error(errorData.error || errorData.message || 'Failed to get generation status.');
    }

    return response.json();
};

/**
 * Sends a request to archive one or more lesson plans.
 * --- REFACTORED ---
 * @param {object} archiveRequest - The ArchiveRequest DTO.
 */
export const archiveLessonPlans = async (archiveRequest) => {
    const response = await fetch(`${API_BASE_URL}/archive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(archiveRequest),
    });
    // Controller returns 204 No Content, so no JSON body
    if (!response.ok) {
        throw new Error('Failed to archive lesson plans');
    }
};
// --- END REFACTORED FUNCTION ---

export const searchAcaraCodes = async (query) => {
    const response = await fetch(`${API_BASE_URL}/curriculum/search?query=${encodeURIComponent(query)}`);
    if (!response.ok) {
        throw new Error('Failed to search ACARA codes');
    }
    return response.json();
};

/**
 * Searches for the best lesson plan templates based on the current wizard config.
 * @param {object} config - The LessonPlanConfig DTO (from formValues).
 * @returns {Promise<Array>} A list of suggested lesson plan templates.
 */
export const searchTemplates = async (config) => {
    const response = await fetch(`${API_BASE_URL}/templates/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to search for templates.');
    }
    return response.json();
};

/**
 * Updates an existing lesson plan with new content (e.g., from adaptations).
 * @param {string} lessonPlanUuid - The UUID of the lesson plan to update.
 * @param {object} lessonContent - The complete, modified lesson plan content object.
 * @returns {Promise<void>}
 */
export const updateLessonPlan = async (lessonPlanUuid, lessonContent) => {
    const response = await fetch(`${API_BASE_URL}/update/${lessonPlanUuid}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(lessonContent), // Send the entire modified lesson content
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update lesson plan.');
    }
};

/**
 * Validates if the lesson plan config can be resolved to a teaching skill.
 * @param {object} config - The LessonPlanConfig DTO (from formValues).
 * @returns {Promise<object>} A success object (e.g., { status: 'valid' }).
 * @throws {Error} Throws an error with the validation message if it fails.
 */
export const validateTeachingSkill = async (config) => {
    const response = await fetch(`${API_BASE_URL}/validate/skill`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || 'Teaching skill validation failed.');
    }

    return response.json();
};

/**
 * Generates and downloads a .docx file for a given lesson plan.
 * @param {string} lessonPlanUuid - The UUID of the lesson plan to download.
 * @returns {Promise<{blob: Blob, filename: string}>} An object with the file blob and filename.
 */
export const downloadWordDocument = async (lessonPlanUuid) => {
    const response = await fetch(`${API_BASE_URL}/generate/word?lessonPlanUuid=${encodeURIComponent(lessonPlanUuid)}`);

    if (!response.ok) {
        // Try to get error message from body, which might be JSON
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to download Word document (status: ${response.status})`);
    }

    // Get blob data (the file)
    const blob = await response.blob();

    // Get filename from the Content-Disposition header
    const header = response.headers.get('content-disposition');
    let filename = 'lesson_plan.docx'; // Default filename
    if (header) {
        const parts = header.split('filename=');
        if (parts.length > 1) {
            filename = parts[1].replace(/"/g, ''); // Remove surrounding quotes
        }
    }

    return { blob, filename };
};